import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';

import { NewUserFactory as UserFactory } from 'app/factories';
import {
    HeaderContent,
    HeaderContentsQuery,
    UserHeaderData,
    UserHeaderDataQuery,
} from 'app/graphql/generated/types-user';
import { User } from 'app/interfaces';
import { AuthService } from 'app/services/auth';
import { ErrorService } from 'app/services/error';
import { NavbarService } from 'app/services/navbar/navbar.service';
import { getUserMenuPrimary, MenuItem } from 'app/services/navbar/user-navigation';

@Injectable()
export class NavbarUserService {
    private authService = inject(AuthService);
    private navbarService = inject(NavbarService);
    private userFactory = inject(UserFactory);
    private destroyRef = inject(DestroyRef);
    private errorService = inject(ErrorService);

    private isAdminProfile = signal<boolean>(false);

    primaryMenu = signal<MenuItem[]>([]);
    contents = signal<HeaderContent[]>([]);
    userHash = signal<string | null>(null);

    fetchUserData(): void {
        combineLatest([this.authService.me(), this.userFactory.getUserHeaderData(), this.userFactory.getContents()])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ([me, userHeader, headerContents]: [User, UserHeaderDataQuery, HeaderContentsQuery]) => {
                    if (headerContents.data) {
                        this.handleUserAdminCheck(me, userHeader.data, headerContents.data);
                    }
                },
                error: (error) => this.errorService.handleError(error),
            });
    }

    private handleUserAdminCheck(me: User, user: UserHeaderData, contents: HeaderContent[]) {
        const isMe = this.isMe(user.hash, me);

        this.isAdminProfile.set(isMe);

        if (isMe) {
            this.handleHeaderData(me.hash);
            this.contents.set(contents.filter((content) => content.imageWeb));
        } else {
            this.handleHeaderData(user.hash);
        }

        this.populateUserHeaders(me);
    }

    private isMe(userHash: string, me: User): boolean {
        if (!me) {
            return false;
        }
        return me.hash === userHash;
    }

    private populateUserHeaders(user: User): void {
        const menu = getUserMenuPrimary(user);
        this.primaryMenu.set(menu);
        this.navbarService.setItems(menu);
    }

    private handleHeaderData(hash: string) {
        this.userHash.set(hash);
    }
}
